import {
  AnyFundingRequest,
  FundingRequestStatus,
  IFundingRequest,
  FundingRequestCollection,
  FUNDING_REQUEST_STATUS_COLOUR_MAP,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  IAttachment,
  IReffable,
  subCollection,
  CollectionReference,
  all$,
  WithRef,
  multiFilter,
  Firestore,
  DocumentReference,
} from '@principle-theorem/shared';
import { MonoTypeOperatorFunction, Observable } from 'rxjs';

export class FundingRequest {
  static init<T>(
    overrides: AtLeast<
      IFundingRequest<T>,
      'type' | 'fundingProvider' | 'data' | 'patient'
    >
  ): IFundingRequest<T> {
    return {
      status: FundingRequestStatus.Draft,
      deleted: false,
      ...overrides,
    };
  }

  static attachmentCol(
    fundingRequest: IReffable<AnyFundingRequest>
  ): CollectionReference<IAttachment> {
    return subCollection(
      fundingRequest.ref,
      FundingRequestCollection.Attachments
    );
  }

  static attachments$(
    fundingRequest: IReffable<AnyFundingRequest>
  ): Observable<WithRef<IAttachment>[]> {
    return all$(FundingRequest.attachmentCol(fundingRequest)).pipe(
      multiFilter((attachment) => !attachment.deleted)
    );
  }

  static storagePath(fundingRequest: IReffable<AnyFundingRequest>): string {
    return fundingRequest.ref.path;
  }

  static filterActive$<T extends AnyFundingRequest>(): MonoTypeOperatorFunction<
    WithRef<T>[]
  > {
    return multiFilter((fundingRequest) =>
      [
        FundingRequestStatus.Pending,
        FundingRequestStatus.Submitted,
        FundingRequestStatus.Approved,
      ].includes(fundingRequest.status)
    );
  }

  static async addAttachment(
    fundingRequest: IReffable<AnyFundingRequest>,
    attachment: IAttachment
  ): Promise<void> {
    await Firestore.addDoc(
      FundingRequest.attachmentCol(fundingRequest),
      attachment
    );
  }

  static async removeAttachment(
    attachment: WithRef<IAttachment>
  ): Promise<void> {
    await Firestore.deleteDoc(attachment.ref);
  }

  static getStatusColour(status: FundingRequestStatus): string {
    return FUNDING_REQUEST_STATUS_COLOUR_MAP[status];
  }

  static claimSubmitted(claim: WithRef<AnyFundingRequest>): boolean {
    return claim.status === FundingRequestStatus.Submitted;
  }

  static claimInDraft(claim: WithRef<AnyFundingRequest>): boolean {
    return claim.status === FundingRequestStatus.Draft;
  }

  static canCancel(claim: WithRef<AnyFundingRequest>): boolean {
    return [
      FundingRequestStatus.Draft,
      FundingRequestStatus.Pending,
      FundingRequestStatus.Submitted,
    ].some((status) => status === claim.status);
  }

  static practiceRef(
    fundingRequest: IReffable<AnyFundingRequest>
  ): DocumentReference<IPractice> {
    return Firestore.getParentDocRef<IPractice>(fundingRequest.ref);
  }
}
